import * as React from "react";

export interface SoloplanPreviewViewerProps {
    output: string;
}

// --- Typen ---

interface MasterDataBusinessPartner {
    number?: number;
    externalNumber?: string;
}

interface ConsignmentBusinessPartner {
    name1?: string;
    name2?: string;
    street?: string;
    houseNumber?: string;
    country?: string;
    zipCode?: string;
    city1?: string;
    city2?: string;
    masterDataBusinessPartner?: MasterDataBusinessPartner;
}

interface Weights { effectiveWeightInKilogram?: number; }
interface Size { lengthInMeters?: number; widthInMeters?: number; heightInMeters?: number; }
interface DangerousGoods { carrierNumber?: string; technicalName?: string; additionalText?: string; }

interface ConsignmentItem {
    itemNumber?: number;
    content1?: string;
    content2?: string;
    quantity?: number;
    weights?: Weights;
    size?: Size;
    mark?: string;
    packaging?: string;
    reference1?: string;
    dangerousGoods?: DangerousGoods;
    hazardousMaterial?: number;
    articleQuantity?: number;
    currencyMatchcode?: string;
    eanCode?: string;
    fzgMatchcode?: string;
    fzgDescription?: string;
}

interface ConsignmentTimes {
    loadingDateStart?: string;
    loadingDateEnd?: string;
    deliveryDateStart?: string;
    deliveryDateEnd?: string;
}

interface ConsignmentInformation {
    senderInfo1?: string;
    senderInfo2?: string;
    receiverInfo1?: string;
    info1?: string;
    info2?: string;  info3?: string;  info4?: string;  info5?: string;
    info6?: string;  info7?: string;  info8?: string;  info9?: string;
    info10?: string; info11?: string; info12?: string; info13?: string;
    info14?: string; info15?: string; info16?: string; info17?: string;
    info18?: string; info19?: string; info20?: string; info21?: string;
    info22?: string; info23?: string; info24?: string; info25?: string;
}

interface Consignment {
    actionAttribute?: string;
    externalNumber?: string;
    matchcode?: string;
    sender?: ConsignmentBusinessPartner;
    receiver?: ConsignmentBusinessPartner;
    consignmentItems?: ConsignmentItem[];
    times?: ConsignmentTimes;
    information?: ConsignmentInformation;
    customFields?: any;
}

interface OrderBusinessPartner {
    number?: number;
    externalNumber?: string;
    name1?: string;
    homeEditAddress?: {
        location1?: string;
        zipCode?: string;
        street?: string;
        country?: { isoTwoCharacterCountryCode?: string } | string;
    };
}

interface SoloplanOrder {
    externalNumber?: string;
    orderReference1?: string;
    info?: string;
    date?: string;
    orderDate?: string;
    customer?: OrderBusinessPartner;
    freightPayer?: OrderBusinessPartner;
    consignments?: Consignment[];
    information?: ConsignmentInformation;
}

interface SoloplanExport {
    header?: { sendDate?: string; exportItemReference?: string };
    order?: SoloplanOrder[];
}

// --- Parsing ---

export function parseSoloplanData(output: string): SoloplanExport | null {
    if (!output) return null;
    try {
        const parsed = JSON.parse(output);
        if (Array.isArray(parsed.order) && parsed.header?.exportItemReference !== undefined) {
            return parsed as SoloplanExport;
        }
        return null;
    } catch { return null; }
}

// --- Hilfsfunktionen ---

function fmtDate(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    } catch { return iso; }
}

function fmtTime(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
}

function isUsefulValue(v?: string): boolean {
    if (!v) return false;
    return v.split(",").map(p => p.trim()).some(p => p && p !== "null" && p !== "---");
}

// --- Soloplan-Style Komponenten ---

// Basis-Input wie in Soloplan (weißes Feld, grauer Rahmen)
function SoInput({ value, width, minWidth, color }: {
    value?: string | number | null;
    width?: number;
    minWidth?: number;
    color?: string;
}) {
    return (
        <div style={{
            border: "1px solid #aaa",
            background: color ?? "#fff",
            padding: "1px 3px",
            minHeight: 18,
            minWidth: minWidth ?? 40,
            width: width ? width : undefined,
            fontSize: 11,
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            color: color ? "#fff" : "#000",
        }}>
            {value ?? ""}
        </div>
    );
}

// Info-Textarea wie in Soloplan (label links, mehrzeiliges Feld rechts)
function InfoField({ label, value }: { label: string; value?: string }) {
    const useful = value && value.trim() && value !== "null";
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={{ fontSize: 10, color: "#444", whiteSpace: "nowrap" }}>{label}</span>
            <div style={{
                border: "1px solid #aaa",
                background: useful ? "#fff" : "#f0f0f0",
                minHeight: 36,
                padding: "1px 3px",
                fontSize: 11,
                color: "#222",
                whiteSpace: "pre-wrap",
                overflowY: "auto",
                maxHeight: 52,
            }}>
                {useful ? value : ""}
            </div>
        </div>
    );
}

// Sendungsinformationen als 4-Spalten-Grid wie in Soloplan
function InfoFieldsSection({ info }: { info: ConsignmentInformation }) {
    const [open, setOpen] = React.useState(false);
    // Spalte 1: senderInfo1 (=1.Sendungsinformat), info2-4 (inkl. info4=4.Sendungsinformat), info5-8
    // Spalte 2: info9-16
    // Spalte 3: info17, senderInfo2 (=Absender Info Fahrer), receiverInfo1 (=Empfänger Info Fahrer), info21, info18-20
    // Spalte 4: info22-25
    const col1: [string, string | undefined][] = [
        ["1. Sendungsinformation", info.senderInfo1],
        ["Info2", info.info2],
        ["Info3", info.info3],
        ["Info4", info.info4],
        ["Info5", info.info5],
        ["Info6", info.info6],
        ["Info7", info.info7],
        ["Info8", info.info8],
    ];
    const col2: [string, string | undefined][] = [
        ["Info9",  info.info9],
        ["Info10", info.info10],
        ["Info11", info.info11],
        ["Info12", info.info12],
        ["Info13", info.info13],
        ["Info14", info.info14],
        ["Info15", info.info15],
        ["Info16", info.info16],
    ];
    const col3: [string, string | undefined][] = [
        ["Info17",                info.info17],
        ["Absender Info Fahrer",  info.senderInfo2],
        ["Empfänger Info Fahrer", info.receiverInfo1],
        ["Info21",                info.info21],
        ["Info18",                info.info18],
        ["Info19",                info.info19],
        ["Info20",                info.info20],
        ["Info1",                 info.info1],
    ];
    const col4: [string, string | undefined][] = [
        ["Info22", info.info22],
        ["Info23", info.info23],
        ["Info24", info.info24],
        ["Info25", info.info25],
    ];

    const hasAnyValue = [...col1, ...col2, ...col3, ...col4]
        .some(([, v]) => v && v.trim() && v !== "null");

    if (!hasAnyValue) return null;

    const renderCol = (fields: [string, string | undefined][]) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}>
            {fields.map(([label, value]) => (
                <InfoField key={label} label={label} value={value} />
            ))}
        </div>
    );

    return (
        <div style={{ marginTop: 4 }}>
            <div
                onClick={() => setOpen(o => !o)}
                style={{
                    borderTop: "1px solid #c0c0c0",
                    margin: "4px 0 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    userSelect: "none",
                }}
            >
                <span style={{
                    fontSize: 10,
                    color: "#555",
                    background: "#e8e8e8",
                    padding: "0 4px",
                    marginTop: -7,
                    position: "relative",
                }}>
                    {open ? "▾" : "▸"} Sendungsinformationen
                </span>
            </div>
            {open && (
                <div style={{
                    display: "flex",
                    gap: 6,
                    background: "#f0f0f0",
                    border: "1px solid #c0c0c0",
                    borderTop: "none",
                    padding: 4,
                }}>
                    {renderCol(col1)}
                    {renderCol(col2)}
                    {renderCol(col3)}
                    {renderCol(col4)}
                </div>
            )}
        </div>
    );
}

// Label wie in Soloplan (kleiner grauer Text vor dem Feld)
function SoLabel({ text, bold }: { text: string; bold?: boolean }) {
    return (
        <span style={{
            fontSize: 11,
            color: "#333",
            fontWeight: bold ? "bold" : "normal",
            whiteSpace: "nowrap",
            marginRight: 2,
        }}>
            {text}
        </span>
    );
}

// Formzeile mit optionalem linken Einzug
function FormRow({ children, indent, marginBottom }: {
    children: React.ReactNode;
    indent?: boolean;
    marginBottom?: number;
}) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: marginBottom ?? 2,
            paddingLeft: indent ? 8 : 0,
        }}>
            {children}
        </div>
    );
}

// Trennlinie wie in Soloplan zwischen Sektionen
function Divider({ label }: { label?: string }) {
    return (
        <div style={{
            borderTop: "1px solid #c0c0c0",
            margin: "4px 0",
            display: "flex",
            alignItems: "center",
            gap: 6,
        }}>
            {label && (
                <span style={{
                    fontSize: 10,
                    color: "#666",
                    background: "#e8e8e8",
                    padding: "0 4px",
                    marginTop: -7,
                    position: "relative",
                }}>
                    {label}
                </span>
            )}
        </div>
    );
}

// Partner-Zeile (Absender/Empfänger) — eine Zeile wie in Soloplan
function PartnerRow({ label, partner }: { label: string; partner?: ConsignmentBusinessPartner }) {
    const mdBp = partner?.masterDataBusinessPartner;
    const nr = mdBp?.number ?? mdBp?.externalNumber;

    return (
        <>
            <FormRow>
                <SoLabel text={`${label}:`} bold />
                {nr != null && <SoInput value={nr} width={55} />}
                <SoInput value={partner?.name1} minWidth={180} width={220} />
                {partner?.name2 && <SoInput value={partner.name2} minWidth={140} width={180} />}
                {partner?.street && (
                    <>
                        <SoLabel text="Straße:" />
                        <SoInput value={partner.street} minWidth={140} width={170} />
                    </>
                )}
                {partner?.houseNumber && (
                    <>
                        <SoLabel text="Nr.:" />
                        <SoInput value={partner.houseNumber} width={35} />
                    </>
                )}
                {partner?.country && (
                    <>
                        <SoLabel text="Land:" />
                        <SoInput value={partner.country} width={28} />
                    </>
                )}
                {partner?.zipCode && (
                    <>
                        <SoLabel text="PLZ:" />
                        <SoInput value={partner.zipCode} width={50} />
                    </>
                )}
                {partner?.city1 && (
                    <>
                        <SoLabel text="Ort 1:" />
                        <SoInput value={partner.city1} width={100} />
                    </>
                )}
                {partner?.city2 && (
                    <>
                        <SoLabel text="Ort 2:" />
                        <SoInput value={partner.city2} width={100} />
                    </>
                )}
            </FormRow>
        </>
    );
}

// Rechtes Panel pro Sendung
function RightPanelConsignment({ cons }: { cons: Consignment }) {
    return (
        <div style={{ borderTop: "1px solid #c8c8c8", paddingTop: 4, marginTop: 4 }}>
            <FormRow>
                <SoLabel text="Ext. Sendungen:" />
                <SoInput value={cons.externalNumber} minWidth={100} />
            </FormRow>
            <div style={{
                background: "#d6e0f0",
                border: "1px solid #a0aec0",
                padding: "1px 4px",
                fontSize: 10,
                fontWeight: "bold",
                margin: "3px 0 2px",
            }}>
                Zeiten aus Telematik
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, marginBottom: 2 }}>
                <SoInput value="" width={70} />
                <SoInput value="" width={70} />
                <SoInput value="" width={70} />
                <SoInput value="" width={70} />
            </div>
            <FormRow>
                <SoLabel text="Erd-Ende:" />
                <SoInput value="" minWidth={80} />
            </FormRow>
            <div style={{ marginTop: 3 }}>
                <div style={{ fontSize: 10, color: "#333", marginBottom: 1 }}>Empf-Info und Entladeweise (1):</div>
                <div style={{
                    border: "1px solid #aaa",
                    background: "#fff",
                    minHeight: 32,
                    padding: "1px 3px",
                    fontSize: 11,
                }} />
            </div>
        </div>
    );
}

function ConsignmentView({ cons, index }: { cons: Consignment; index: number }) {
    const t = cons.times;
    const info = cons.information;


    const mainContent = (
        <div style={{ flex: 1, minWidth: 0 }}>
            {/* Absender */}
            <PartnerRow label="Absender" partner={cons.sender} />

            {/* Empfänger */}
            <PartnerRow label="Empfänger" partner={cons.receiver} />

            {/* Entladestelle */}
            <FormRow>
                <SoLabel text="Entladestelle:" />
                <SoInput value="" minWidth={120} />
            </FormRow>

            <Divider />

            {/* Ladedatum */}
            <FormRow>
                <SoLabel text="Ladedatum" bold />
                <SoLabel text="von:" />
                <SoInput value={fmtDate(t?.loadingDateStart)} width={72} />
                <SoInput value={fmtTime(t?.loadingDateStart)} width={40} />
                <SoLabel text="bis:" />
                <SoInput value={fmtDate(t?.loadingDateEnd)} width={72} />
                <SoInput value={fmtTime(t?.loadingDateEnd)} width={40} />
            </FormRow>

            {/* Lieferdatum */}
            <FormRow>
                <SoLabel text="Lieferdatum" bold />
                <SoLabel text="von:" />
                <SoInput value={fmtDate(t?.deliveryDateStart)} width={72} />
                <SoInput value={fmtTime(t?.deliveryDateStart)} width={40} />
                <SoLabel text="bis:" />
                <SoInput value={fmtDate(t?.deliveryDateEnd)} width={72} />
                <SoInput value={fmtTime(t?.deliveryDateEnd)} width={40} />
            </FormRow>

            <Divider />

            {/* Positionstabelle */}
            {cons.consignmentItems && cons.consignmentItems.length > 0 && (
                <div style={{ overflowX: "auto", marginTop: 4 }}>
                    <table style={{
                        borderCollapse: "collapse",
                        fontSize: 11,
                        width: "100%",
                        background: "#fff",
                    }}>
                        <thead>
                            <tr style={{ background: "#e0e0e0" }}>
                                {["#", "Anzahl", "Art", "Typ", "Hersteller",
                                  "Fg.-Nr.", "Aufbau-Nr.", "Kd.-Referenz",
                                  "L (m)", "B (m)", "H (m)", "Gewicht (kg)", "Referenz"].map(h => (
                                    <th key={h} style={{
                                        border: "1px solid #aaa",
                                        padding: "1px 4px",
                                        textAlign: "left",
                                        fontWeight: "normal",
                                        whiteSpace: "nowrap",
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {cons.consignmentItems.map((item, i) => {
                                const hersteller = item.dangerousGoods?.technicalName
                                    || item.dangerousGoods?.additionalText
                                    || item.fzgDescription || "";
                                return (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f8f8" }}>
                                        {[
                                            item.itemNumber ?? i + 1,
                                            item.articleQuantity ?? item.quantity ?? "",
                                            item.packaging ?? "",
                                            item.content1 ?? "",
                                            hersteller,
                                            item.mark ?? "",
                                            item.eanCode ?? "",
                                            item.content2 ?? "",
                                            item.size?.lengthInMeters ?? "",
                                            item.size?.widthInMeters ?? "",
                                            item.size?.heightInMeters ?? "",
                                            item.weights?.effectiveWeightInKilogram ?? "",
                                            item.reference1 ?? "",
                                        ].map((val, ci) => (
                                            <td key={ci} style={{
                                                border: "1px solid #ccc",
                                                padding: "1px 4px",
                                                whiteSpace: "nowrap",
                                            }}>{val}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {info && <InfoFieldsSection info={info} />}
        </div>
    );

    return (
        <div style={{ display: "flex", gap: 0, marginBottom: 6, alignItems: "stretch" }}>
            {/* Linke Spalte: Sendungs-Header + Hauptinhalt */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    background: "#d6e0f0",
                    border: "1px solid #a0aec0",
                    borderBottom: "none",
                    padding: "1px 6px",
                    fontSize: 11,
                    fontWeight: "bold",
                }}>
                    Sendung {index + 1} &nbsp;|&nbsp; Ext. Nr.: {cons.externalNumber ?? "—"}
                    {cons.customFields?.customTypeValue1?.matchcode
                        ? ` | ${cons.customFields.customTypeValue1.matchcode}` : ""}
                </div>
                <div style={{ border: "1px solid #a0aec0", padding: "3px 4px" }}>
                    {mainContent}
                </div>
            </div>
            {/* Rechte Spalte */}
            <div style={{
                width: 200,
                flexShrink: 0,
                border: "1px solid #a0aec0",
                borderLeft: "none",
                padding: "3px 4px",
                background: "#f8f8f8",
            }}>
                <RightPanelConsignment cons={cons} />
            </div>
        </div>
    );
}

// Rechtes Panel auf Auftragsebene (Ursprung, Bearbeitung, Pauschalbetrag)
function RightPanelOrder() {
    return (
        <div style={{
            width: 200,
            flexShrink: 0,
            borderLeft: "none",
            padding: "3px 4px",
            background: "#f8f8f8",
            border: "1px solid #b0b0b0",
            borderBottom: "none",
        }}>
            <FormRow><SoLabel text="Ursprung:" /><SoInput value="" minWidth={90} /></FormRow>
            <FormRow><SoLabel text="Bearbeitung:" /><SoInput value="" minWidth={80} /></FormRow>
            <FormRow><SoLabel text="Info:" /><SoInput value="" minWidth={80} /></FormRow>
            <Divider />
            <FormRow><SoLabel text="Pauschalbetrag:" /><SoInput value="" width={60} /></FormRow>
            <FormRow><SoLabel text="Straße Sek:" /><SoInput value="" minWidth={80} /></FormRow>
        </div>
    );
}

export function SoloplanPreviewViewer({ output }: SoloplanPreviewViewerProps) {
    const data = React.useMemo(() => parseSoloplanData(output), [output]);

    if (!data) {
        return (
            <div style={{ color: "#888", fontStyle: "italic", padding: 16, fontSize: 12, fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
                Kein Soloplan-Auftrag erkannt.
            </div>
        );
    }

    const orders = data.order ?? [];

    return (
        <div style={{
            fontFamily: "Segoe UI, Tahoma, Arial, sans-serif",
            fontSize: 11,
            background: "#e8e8e8",
            padding: 6,
            overflowY: "auto",
            height: "100%",
            boxSizing: "border-box",
        }}>
            {orders.map((ord, oi) => (
                <div key={oi} style={{ marginBottom: 8 }}>
                    {/* Auftrags-Header: links Felder, rechts Ursprung/Bearbeitung */}
                    <div style={{ display: "flex", alignItems: "stretch" }}>
                        <div style={{
                            flex: 1,
                            background: "#f0f0f0",
                            border: "1px solid #b0b0b0",
                            borderRight: "none",
                            borderBottom: "none",
                            padding: "4px 6px",
                        }}>
                            <FormRow marginBottom={2}>
                                <SoLabel text="Auftragsdatum:" />
                                <SoInput value={fmtDate(ord.orderDate ?? ord.date)} width={72} />
                                <SoLabel text="Ext. Auftragnr.:" />
                                <SoInput value={ord.externalNumber} minWidth={120} color="#c8a000" />
                                {ord.orderReference1 && <>
                                    <SoLabel text="Ref.:" />
                                    <SoInput value={ord.orderReference1} minWidth={80} />
                                </>}
                                {ord.info && <>
                                    <SoLabel text="Info:" />
                                    <SoInput value={ord.info} minWidth={60} />
                                </>}
                            </FormRow>
                            <FormRow marginBottom={2}>
                                <SoLabel text="Auftraggeber:" />
                                <SoInput value={ord.customer?.number ?? ord.customer?.externalNumber} width={65} />
                                {ord.customer?.name1 && <SoInput value={ord.customer.name1} minWidth={120} />}
                            </FormRow>
                            <FormRow marginBottom={2}>
                                <SoLabel text="Frachtzahler:" />
                                <SoInput value={ord.freightPayer?.number ?? ord.freightPayer?.externalNumber} width={65} />
                                {ord.freightPayer?.name1 && <SoInput value={ord.freightPayer.name1} minWidth={120} />}
                            </FormRow>
                            <Divider />
                            <FormRow marginBottom={2}>
                                <SoLabel text="Ladestelle:" />
                                <SoInput value="" minWidth={120} />
                            </FormRow>
                            {ord.information && <InfoFieldsSection info={ord.information} />}
                        </div>
                        <RightPanelOrder />
                    </div>

                    {/* Sendungen */}
                    {(ord.consignments ?? []).map((cons, ci) => (
                        <ConsignmentView key={ci} cons={cons} index={ci} />
                    ))}
                </div>
            ))}
        </div>
    );
}
